// src/components/ProgrammaMieCommesse.js
// ═══════════════════════════════════════════════════════════════════════════
// HOME DEL PM: «PROGRAMMA LAVORI DELLE MIE COMMESSE» (ottobre 2026, W424)
// ═══════════════════════════════════════════════════════════════════════════
// La Programmazione settimanale vista per commessa: righe = commesse di cui sono
// responsabile, colonne = giorni, celle = persone. Settimana in corso, frecce per le altre.
// Regole in utils/programmaPM.js (giorni pubblicati, righe nascoste, niente furgoni).
// Titolare e admin vedono tutte le commesse. Compare solo se c'è qualcosa da mostrare
// o se la persona è responsabile di almeno una commessa aperta.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useCanSee } from "../hooks/useCanSee";
import { programmaPM, lunediDi, idSettimana } from "../utils/programmaPM";

const VIOLA = "#7c3aed", SCURO = "#1e1b4b", BORDO = "#e5e7eb";

export default function ProgrammaMieCommesse({ progetti = [], onNavigate }) {
  const { currentUser, userProfile, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId: orgId } = useOrg();
  const canSee = useCanSee();
  const tutte = isOrgAdmin || isPlatformOwner;
  const programma = tutte || canSee("modifica-programmazione");
  const uid = currentUser?.uid;
  const mieAperte = progetti.filter(p => !p.isParent && !p.archived && p.statoCommessa !== "chiusa" && (tutte || p.responsabileUid === uid));

  const [lunedi, setLunedi] = useState(() => lunediDi(new Date()));
  const [settimana, setSettimana] = useState(undefined);
  const [privacy, setPrivacy] = useState({});
  const [utenti, setUtenti] = useState([]);
  // W425: il riquadro si vede SEMPRE (deciso con Paolo), anche senza commesse o senza persone
  // programmate: in quel caso spiega perché è vuoto. Unica eccezione: gli operativi che non
  // sono responsabili di nessuna commessa (per loro c'è la Programmazione).
  const nascosto = !tutte && mieAperte.length === 0 && (userProfile?.ruoloOperativo || "operativi") === "operativi";
  const attivo = !!orgId && !!uid && mieAperte.length > 0;
  useEffect(() => {
    if (!attivo) return undefined;
    setSettimana(undefined);
    return onSnapshot(doc(db, "programmazioniSettimanali", idSettimana(orgId, lunedi)), s => setSettimana(s.exists() ? s.data() : null), () => setSettimana(null));
  }, [attivo, orgId, lunedi]);
  useEffect(() => {
    if (!attivo) return undefined;
    return onSnapshot(doc(db, "programmazioniPrivacy", orgId), s => setPrivacy(s.exists() ? s.data() : {}), () => setPrivacy({}));
  }, [attivo, orgId]);
  useEffect(() => {
    if (!attivo) return undefined;
    return onSnapshot(query(collection(db, "users"), where("organizationId", "==", orgId)), s => setUtenti(s.docs.map(d => ({ uid: d.id, ...d.data() }))), () => setUtenti([]));
  }, [attivo, orgId]);

  const dati = useMemo(() => (settimana === undefined ? null : programmaPM({
    lunedi, programmazione: settimana, privacy, progetti, utenti, pmUid: tutte ? null : uid, vedeNonPubblicati: programma,
  })), [settimana, lunedi, privacy, progetti, utenti, tutte, uid, programma]);

  if (!orgId || !uid || nascosto) return null;
  const sposta = (n) => setLunedi(l => { const x = new Date(l); x.setDate(x.getDate() + 7 * n); return x; });
  const domenica = new Date(lunedi); domenica.setDate(domenica.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
  const settCorrente = lunedi.getTime() === lunediDi(new Date()).getTime();
  const btn = { border: `1px solid ${BORDO}`, background: "#fff", borderRadius: 7, padding: "4px 10px", cursor: "pointer", fontSize: 13, fontFamily: "inherit", color: "#374151" };
  const apri = (projectId) => onNavigate && onNavigate({ page: "project", projectId });

  return (
    <section aria-label="Programma lavori delle mie commesse" style={{ background: "#fff", border: `1px solid ${BORDO}`, borderRadius: 14, padding: "14px 16px", marginBottom: 18 }}>
      <style>{`
        .pmc-tab { width: 100%; border-collapse: collapse; font-size: 12.5px; }
        .pmc-tab th, .pmc-tab td { border: 1px solid ${BORDO}; padding: 6px 8px; vertical-align: top; text-align: left; }
        .pmc-tab th { background: #f5f3ff; color: #4c1d95; font-weight: 700; white-space: nowrap; }
        .pmc-lista { display: none; }
        @media (max-width: 700px) { .pmc-scroll { display: none; } .pmc-lista { display: grid; gap: 8px; } }
      `}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <div style={{ flex: "1 1 240px" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: SCURO }}>📅 Programma lavori {tutte ? "delle commesse" : "delle mie commesse"}</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
            {fmt(lunedi)} – {fmt(domenica)}{dati ? ` · ${dati.assegnazioni} ${dati.assegnazioni === 1 ? "assegnazione" : "assegnazioni"}` : ""} · dalla Programmazione settimanale
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button type="button" style={btn} onClick={() => sposta(-1)} aria-label="Settimana precedente">‹</button>
          {!settCorrente && <button type="button" style={btn} onClick={() => setLunedi(lunediDi(new Date()))}>Questa settimana</button>}
          <button type="button" style={btn} onClick={() => sposta(1)} aria-label="Settimana successiva">›</button>
        </div>
      </div>
      {!attivo ? <div style={{ fontSize: 12.5, color: "#6b7280" }}>Non risulti responsabile di nessuna commessa aperta: qui compare il programma delle commesse in cui sei indicato come «Responsabile».</div>
        : !dati ? <div style={{ fontSize: 12.5, color: "#9ca3af" }}>Carico il programma…</div>
        : dati.righe.length === 0 ? <div style={{ fontSize: 12.5, color: "#6b7280" }}>Nessuna persona programmata sulle {tutte ? "commesse" : "tue commesse"} in questa settimana{programma ? "" : " (o i giorni non sono ancora pubblicati)"}.</div>
        : (<>
          <div className="pmc-scroll" style={{ overflowX: "auto" }}>
            <table className="pmc-tab">
              <thead><tr><th scope="col">Commessa</th>{dati.giorni.map(g => <th key={g.data} scope="col">{g.etichetta}{programma && !g.pubblicato ? " 🔒" : ""}</th>)}</tr></thead>
              <tbody>
                {dati.righe.map(r => (
                  <tr key={r.projectId}>
                    <th scope="row" style={{ background: "#fff", fontWeight: 600, whiteSpace: "normal", minWidth: 180 }}>
                      <button type="button" onClick={() => apri(r.projectId)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", fontFamily: "inherit", fontSize: 12.5, color: SCURO }}>
                        {r.codice && <b style={{ color: VIOLA }}>{r.codice} </b>}{r.nome}
                      </button>
                      {r.cliente && <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 400 }}>{r.cliente}</div>}
                    </th>
                    {dati.giorni.map(g => <td key={g.data}>{(r.celle[g.data] || []).map(n => <div key={n}>{n}</div>)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pmc-lista">
            {dati.giorni.filter(g => dati.righe.some(r => r.celle[g.data])).map(g => (
              <div key={g.data} style={{ border: `1px solid ${BORDO}`, borderRadius: 10, padding: "8px 10px" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#4c1d95" }}>{g.etichetta.charAt(0).toUpperCase() + g.etichetta.slice(1)}</div>
                {dati.righe.filter(r => r.celle[g.data]).map(r => (
                  <div key={r.projectId} style={{ fontSize: 13, marginTop: 4 }}>
                    <button type="button" onClick={() => apri(r.projectId)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 13, color: VIOLA, fontWeight: 700 }}>{r.codice || r.nome}</button>
                    <span style={{ color: "#374151" }}>: {r.celle[g.data].join(", ")}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>)}
    </section>
  );
}
